import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { trackedFunction } from 'reactiveweb/function';
import { t } from 'ember-intl';
import {
  PhChartPieSlice,
  PhClockCounterClockwise,
  PhMagnifyingGlass,
  PhStar,
  PhTable,
} from 'ember-phosphor-icons';
import Heading from 'tio-ui/components/heading';
import IconBadge from 'tio-ui/components/icon-badge';
import ReportStatsModal from '../report-stats-modal';
import PartnerReportRow, { type PartnerRowItem } from './report-row';
import { PRODUCT_BADGES } from '../reports/product-icon-badge';
import {
  PRODUCT_LABELS,
  PRODUCT_SECTION_ORDER,
  VALID_REPORT_TYPES,
  type ProductCode,
} from '../../utils/dataset-utils';
import { localizedRelativeTime } from '../../utils/relative-time';
import type { Store as StoreService } from '@warp-drive/core';
import type IntlService from 'ember-intl/services/intl';
import type SessionContextService from 'tio-common/services/session-context';
import type PartnerReportsPreferencesService from '../../services/partner-reports-preferences';
import type { ReportsExtension as Report } from 'tio-data/data/resources/reports.ext';
import type { ReportTracking } from 'tio-data/data/resources/report-tracking.type';
import { query } from '@warp-drive/utilities/json-api';

/** One server page. Search filters only what was fetched, so rows beyond this are unreachable. */
const REPORT_PAGE_LIMIT = 200;

/** Dashboards are reports by storage only — their search_config holds a widget grid. */
const DASHBOARD_REPORT_TYPE = 'dashboard';

/** Both shortcut cards cap here so they stay a similar height. */
const CARD_ROW_LIMIT = 4;

/** Strategy Finder is not a paid product; the admin builder's pickers still distinguish it. */
const PARTNER_PRODUCT_ALIASES: Partial<Record<ProductCode, ProductCode>> = {
  STRATEGYFINDER: 'Core',
};

/** The shared PRODUCT_LABELS are the admin builder's; SYF stays "Secure" per employee-facing copy. */
const PARTNER_PRODUCT_LABELS: Partial<Record<ProductCode, string>> = {
  PSLF: 'Public Service Loan Forgiveness',
  SLR: 'Student Loan Repayment',
  Core: 'Core Platform',
};

interface PartnerConsolidatedReportsPageSignature {
  Args: {
    companyId: string;
    viewerRoute: string;
    /** Lowercase product codes to list; omit for every builder product. */
    reportTypes?: string[];
    /** Live search text, owned by the route template so the input can sit in PageHeader's :toolbar. */
    searchQuery?: string;
    /** Takes the dashboard's report id as its one dynamic segment. Omit to leave dashboards out. */
    dashboardViewerRoute?: string;
  };
}

export default class PartnerConsolidatedReportsPage extends Component<PartnerConsolidatedReportsPageSignature> {
  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare partnerReportsPreferences: PartnerReportsPreferencesService;

  /** Report whose usage-stats modal is open (null = closed) */
  @tracked statsReport: Report | null = null;

  constructor(owner: Owner, args: PartnerConsolidatedReportsPageSignature['Args']) {
    super(owner, args);
    void this.partnerReportsPreferences.load.perform();
  }

  openStats = (item: PartnerRowItem) => {
    this.statsReport = this.publishedById.get(String(item.id)) ?? null;
  };

  closeStats = () => {
    this.statsReport = null;
  };

  togglePin = (item: PartnerRowItem) => {
    this.partnerReportsPreferences.togglePin(item.id);
  };

  isPinned = (id: string | null) => this.partnerReportsPreferences.isPinned(id);

  get canPin() {
    return this.partnerReportsPreferences.canPin;
  }

  /** FAIL CLOSED: only an OMITTED arg means all products; an empty list allows nothing. */
  get effectiveReportTypes(): string[] {
    const requested = this.args.reportTypes;
    if (requested === undefined) return [...VALID_REPORT_TYPES];
    return [
      ...new Set(
        requested.map((type) => type.toLowerCase()).filter((type) => VALID_REPORT_TYPES.has(type))
      ),
    ];
  }

  get includesDashboards() {
    return Boolean(this.args.dashboardViewerRoute);
  }

  /** The product types, plus dashboards only when the host gave them somewhere to open. */
  get fetchedReportTypes() {
    return this.includesDashboards
      ? [...this.effectiveReportTypes, DASHBOARD_REPORT_TYPE]
      : this.effectiveReportTypes;
  }

  reportsData = trackedFunction(this, async () => {
    if (this.fetchedReportTypes.length === 0) return { rows: [], total: 0 };

    const { content } = (await this.store.request(
      query<Report>(
        'report',
        {
          'page[limit]': REPORT_PAGE_LIMIT,
          'filter[report_type]': this.fetchedReportTypes.join(','),
        },
        { namespace: 'api/v2' }
      )
    )) as { content: { data: Report[]; meta?: { page?: { total?: number } } } };
    // Absent meta reads as "no more than we got", so the notice stays off rather than guessing.
    return { rows: content.data, total: content.meta?.page?.total ?? content.data.length };
  });

  // The server confines a non-super-admin to their own rows, so person_id only narrows.
  recentTrackings = trackedFunction(this, async () => {
    const personId = this.sessionContext.user?.person?.id;
    if (!personId) return [];
    const { content } = (await this.store.request({
      ...query<ReportTracking>(
        'report-tracking',
        { 'filter[person_id]': personId, 'page[limit]': 50 },
        { namespace: 'api/v2' }
      ),
      cacheOptions: { reload: true }, // else a just-viewed report misses Recent for 5 minutes
    })) as { content: { data: ReportTracking[] } };
    return content.data;
  });

  get isLoading() {
    return this.reportsData.isLoading;
  }

  /** A rejected request leaves `value` empty, which would otherwise read as a company with no reports. */
  get loadFailed() {
    return Boolean(this.reportsData.error);
  }

  get isTruncated() {
    const loaded = this.reportsData.value;
    return Boolean(loaded && loaded.total > loaded.rows.length);
  }

  reportPageLimit = REPORT_PAGE_LIMIT;

  /** Placeholder rows while the listing loads; the count is cosmetic. */
  skeletonRows = [1, 2, 3];

  #isDashboard(report: Report) {
    return (report.reportType || '').toLowerCase() === DASHBOARD_REPORT_TYPE;
  }

  /** Published, non-archived reports and dashboards within the fetched types. */
  @cached
  get publishedReports() {
    const all = this.reportsData.value?.rows ?? [];
    const allowedTypes = this.fetchedReportTypes;
    // No visibility filter: ReportPolicy::Scope already narrowed this to the viewer's company.
    return all.filter(
      (r) =>
        allowedTypes.includes((r.reportType || '').toLowerCase()) && r.isPublished && !r.isArchived
    );
  }

  @cached
  get publishedById() {
    return new Map(this.publishedReports.map((report) => [String(report.id), report]));
  }

  get trimmedQuery() {
    return (this.args.searchQuery ?? '').trim().toLowerCase();
  }

  get hasQuery() {
    return this.trimmedQuery.length > 0;
  }

  /** Matches name and description, the two fields a partner admin can actually see on a row. */
  #matches = (item: PartnerRowItem) => {
    const needle = this.trimmedQuery;
    if (!needle) return true;
    return (
      (item.name || '').toLowerCase().includes(needle) ||
      (item.description || '').toLowerCase().includes(needle)
    );
  };

  get matchingDashboards() {
    return this.publishedReports.filter((r) => this.#isDashboard(r) && this.#matches(r));
  }

  /** Dashboards never fall into a product group — they would otherwise land under Core. */
  get matchingDetailed() {
    return this.publishedReports.filter((r) => !this.#isDashboard(r) && this.#matches(r));
  }

  #productOf(report: Report): ProductCode {
    const rawType = (report.reportType || '').toUpperCase() as ProductCode;
    const known = PRODUCT_LABELS[rawType] ? rawType : 'Core';
    return PARTNER_PRODUCT_ALIASES[known] ?? known;
  }

  #labelFor(code: ProductCode) {
    return PARTNER_PRODUCT_LABELS[code] ?? PRODUCT_LABELS[code];
  }

  /** Groups in the fixed product order; a product with no matches is omitted entirely. */
  get reportsByProduct() {
    const grouped = new Map<ProductCode, Report[]>();
    for (const report of this.matchingDetailed) {
      const product = this.#productOf(report);
      const list = grouped.get(product) ?? [];
      list.push(report);
      grouped.set(product, list);
    }
    return PRODUCT_SECTION_ORDER.filter((code) => grouped.has(code)).map((code) => {
      // Badge the resolved group, never a report: an aliased one carries its own product.
      return {
        product: code,
        label: this.#labelFor(code),
        reports: grouped.get(code) ?? [],
        badge: PRODUCT_BADGES[code],
      };
    });
  }

  get matchCount() {
    return this.matchingDetailed.length;
  }

  /** A query that filtered every section out — distinct from a company with nothing at all. */
  get hasNoMatches() {
    return this.hasQuery && this.matchingDashboards.length + this.matchingDetailed.length === 0;
  }

  get isEmpty() {
    return !this.hasQuery && this.publishedReports.length === 0;
  }

  #metaFor(report: Report) {
    return this.#isDashboard(report)
      ? this.intl.t('reporting.dashboards')
      : this.#labelFor(this.#productOf(report));
  }

  /** Newest pin first, dropping any the viewer can no longer see. */
  get pinnedItems() {
    return this.partnerReportsPreferences.pinnedReportIds
      .map((id) => this.publishedById.get(id))
      .filter((report): report is Report => Boolean(report))
      .slice(0, CARD_ROW_LIMIT)
      .map((report) => ({ report, meta: this.#metaFor(report) }));
  }

  /** One row per report, newest view first, skipping reports the viewer can no longer see. */
  @cached
  get recentItems() {
    const locale = this.intl.primaryLocale ?? 'en-us';
    const seen = new Set<string>();
    const recents: Array<{ report: Report; meta: string }> = [];
    for (const tracking of this.recentTrackings.value ?? []) {
      const id = String(tracking.reportId);
      if (seen.has(id)) continue;
      seen.add(id);
      const report = this.publishedById.get(id);
      if (!report) continue;
      recents.push({ report, meta: localizedRelativeTime(tracking.createdAt, locale) });
      if (recents.length >= CARD_ROW_LIMIT) break;
    }
    return recents;
  }

  /** Nothing to pin on a page with no reports, so the hint would point at nothing. */
  get showPinned() {
    return this.canPin && this.publishedReports.length > 0;
  }

  /** A lone card should span the row, as auto-fit would, rather than sit at half width. */
  get bothShortcuts() {
    return this.showPinned && this.recentItems.length > 0;
  }

  get showShortcuts() {
    return !this.hasQuery && (this.showPinned || this.recentItems.length > 0);
  }

  queryFor = (report: Report) => {
    return {
      reportId: report.id,
      companyId: this.args.companyId,
    };
  };

  /** A dashboard opens in the view-only dashboard route; everything else in the report viewer. */
  linkFor = (report: Report) => {
    if (this.#isDashboard(report)) {
      return { route: this.args.dashboardViewerRoute ?? '', models: [report.id], query: {} };
    }
    return { route: this.args.viewerRoute, models: [], query: this.queryFor(report) };
  };

  <template>
    {{#if this.isLoading}}
      <div class="tio-col-loose" aria-busy="true" data-test-partner-reports-skeleton>
        <span class="sr-only">{{t "reporting.loading"}}</span>
        {{#each this.skeletonRows}}
          <div class="tio-row rounded-xl border tio-border-default tio-bg-surface px-4 py-3">
            <div class="h-8 w-8 shrink-0 rounded-lg tio-bg-sunken"></div>
            <div class="tio-col-extra-tight min-w-0 flex-1">
              <div class="h-4 w-1/3 rounded tio-bg-sunken"></div>
              <div class="h-3 w-2/3 rounded tio-bg-sunken"></div>
            </div>
          </div>
        {{/each}}
      </div>
    {{else}}
      <div class="tio-col-loose">
        {{#if this.loadFailed}}
          <div class="py-8 text-center" role="alert" data-test-partner-reports-error>
            <p class="tio-fg-danger">{{t "reporting.load_error"}}</p>
          </div>
        {{else}}
          {{#if this.showShortcuts}}
            <div
              class="grid gap-4 {{if this.bothShortcuts 'sm:grid-cols-2'}}"
              data-test-partner-reports-shortcuts
            >
              {{#if this.showPinned}}
                <section
                  class="rounded-2xl border tio-border-default tio-bg-surface p-4"
                  data-test-partner-reports-pinned
                >
                  <div class="tio-row-tight">
                    <PhStar @weight="fill" class="h-4 w-4 tio-fg-warning" aria-hidden="true" />
                    <Heading @level={{2}} @role="panel-title">{{t "reporting.pinned"}}</Heading>
                  </div>
                  {{#if this.pinnedItems.length}}
                    <ul class="tio-col-tight mt-3">
                      {{#each this.pinnedItems as |item|}}
                        {{#let (this.linkFor item.report) as |link|}}
                          <li>
                            <LinkTo
                              class="tio-row-tight tio-text-sm-normal rounded-lg px-2 py-1 tio-fg-primary hover:tio-bg-sunken"
                              @route={{link.route}}
                              @models={{link.models}}
                              @query={{link.query}}
                              data-test-partner-reports-pinned-row={{item.report.id}}
                            >
                              <PhTable class="h-4 w-4 shrink-0 tio-fg-brand" aria-hidden="true" />
                              <span class="min-w-0 flex-1 truncate">{{item.report.name}}</span>
                              <span
                                class="tio-text-xs-normal shrink-0 tio-fg-muted"
                              >{{item.meta}}</span>
                            </LinkTo>
                          </li>
                        {{/let}}
                      {{/each}}
                    </ul>
                  {{else}}
                    <p
                      class="tio-text-sm-normal mt-3 tio-fg-secondary"
                      data-test-partner-reports-pinned-empty
                    >{{t "reporting.pinned_empty"}}</p>
                  {{/if}}
                </section>
              {{/if}}

              {{#if this.recentItems.length}}
                <section
                  class="rounded-2xl border tio-border-default tio-bg-surface p-4"
                  data-test-partner-reports-recent
                >
                  <div class="tio-row-tight">
                    <PhClockCounterClockwise class="h-4 w-4 tio-fg-muted" aria-hidden="true" />
                    <Heading @level={{2}} @role="panel-title">
                      {{t "reporting.recently_viewed"}}
                    </Heading>
                  </div>
                  <ul class="tio-col-tight mt-3">
                    {{#each this.recentItems as |item|}}
                      {{#let (this.linkFor item.report) as |link|}}
                        <li>
                          <LinkTo
                            class="tio-row-tight tio-text-sm-normal rounded-lg px-2 py-1 tio-fg-primary hover:tio-bg-sunken"
                            @route={{link.route}}
                            @models={{link.models}}
                            @query={{link.query}}
                            data-test-partner-reports-recent-row={{item.report.id}}
                          >
                            <PhTable class="h-4 w-4 shrink-0 tio-fg-muted" aria-hidden="true" />
                            <span class="min-w-0 flex-1 truncate">{{item.report.name}}</span>
                            <span
                              class="tio-text-xs-normal shrink-0 tio-fg-muted"
                            >{{item.meta}}</span>
                          </LinkTo>
                        </li>
                      {{/let}}
                    {{/each}}
                  </ul>
                </section>
              {{/if}}
            </div>
          {{/if}}

          {{#if this.matchingDashboards.length}}
            <section data-test-partner-reports-dashboards>
              <div class="tio-row-tight">
                <Heading @level={{2}} @role="section-title">{{t "reporting.dashboards"}}</Heading>
                <span
                  class="tio-text-xs-semibold rounded-full tio-bg-sunken px-2 py-1 tio-fg-muted"
                  data-test-partner-reports-dashboards-count
                >{{this.matchingDashboards.length}}</span>
              </div>
              <div class="tio-col-tight mt-4">
                {{#each this.matchingDashboards as |dashboard|}}
                  {{#let (this.linkFor dashboard) as |link|}}
                    <PartnerReportRow
                      @report={{dashboard}}
                      @route={{link.route}}
                      @models={{link.models}}
                      @icon={{PhChartPieSlice}}
                      @onStats={{this.openStats}}
                      @isPinned={{this.isPinned dashboard.id}}
                      @onTogglePin={{if this.canPin this.togglePin}}
                    />
                  {{/let}}
                {{/each}}
              </div>
            </section>
          {{/if}}

          {{#if this.matchingDetailed.length}}
            <section data-test-partner-reports-detailed>
              <div class="tio-row-tight">
                <Heading @level={{2}} @role="section-title">
                  {{t "reporting.detailed_reports"}}
                </Heading>
                <span
                  class="tio-text-xs-semibold rounded-full tio-bg-sunken px-2 py-1 tio-fg-muted"
                  data-test-partner-reports-count
                >{{this.matchCount}}</span>
              </div>
              <p class="tio-text-sm-normal mt-1 tio-fg-secondary">
                {{t "reporting.detailed_reports_subtitle"}}
              </p>

              <div class="tio-col-loose mt-4">
                {{#each this.reportsByProduct as |section|}}
                  <div class="tio-col-tight" data-test-partner-reports-group={{section.product}}>
                    <div class="tio-row-tight">
                      <IconBadge
                        @icon={{section.badge.icon}}
                        @color={{section.badge.color}}
                        @size="sm"
                        class="shrink-0"
                      />
                      <Heading @level={{3}} @role="panel-title">{{section.label}}</Heading>
                      <span class="tio-text-xs-normal tio-fg-muted">
                        {{t "reporting.report_count" count=section.reports.length}}
                      </span>
                    </div>

                    {{#each section.reports as |report|}}
                      <PartnerReportRow
                        @report={{report}}
                        @route={{@viewerRoute}}
                        @query={{this.queryFor report}}
                        @onStats={{this.openStats}}
                        @isPinned={{this.isPinned report.id}}
                        @onTogglePin={{if this.canPin this.togglePin}}
                      />
                    {{/each}}
                  </div>
                {{/each}}
              </div>
            </section>
          {{/if}}

          {{#if this.hasNoMatches}}
            <div
              class="rounded-2xl border border-dashed tio-border-strong tio-bg-surface px-6 py-10 text-center"
              data-test-partner-reports-no-matches
            >
              <PhMagnifyingGlass class="mx-auto h-7 w-7 tio-fg-muted" />
              <p class="tio-text-base-semibold mt-3 tio-fg-primary">
                {{t "reporting.no_match_title" query=@searchQuery}}
              </p>
              <p class="tio-text-sm-normal mt-1 tio-fg-secondary">{{t
                  "reporting.no_match_hint"
                }}</p>
            </div>
          {{/if}}

          {{#if this.isEmpty}}
            <div class="py-8 text-center" data-test-partner-reports-empty>
              <p class="tio-fg-muted">{{t "reporting.no_reports"}}</p>
            </div>
          {{/if}}
        {{/if}}

      </div>
    {{/if}}

    {{#if this.isTruncated}}
      <p
        class="tio-text-xs-normal tio-fg-muted py-2 text-center"
        data-test-partner-reports-truncated
      >{{t "reporting.truncated_notice" limit=this.reportPageLimit}}</p>
    {{/if}}

    <ReportStatsModal
      @report={{this.statsReport}}
      @companyId={{@companyId}}
      @onClose={{this.closeStats}}
    />
  </template>
}
